import { Container } from 'pixi.js';
import type { Vector2 } from '../core/constants';

export class Camera {
  private container: Container;
  private viewportWidth: number;
  private viewportHeight: number;
  private worldWidth: number;
  private worldHeight: number;

  private targetPosition: Vector2 = { x: 0, y: 0 };
  private currentPosition: Vector2 = { x: 0, y: 0 };
  private zoom = 1;

  constructor(
    container: Container,
    viewportWidth: number,
    viewportHeight: number,
    worldWidth: number,
    worldHeight: number,
  ) {
    this.container = container;
    this.viewportWidth = viewportWidth;
    this.viewportHeight = viewportHeight;
    this.worldWidth = worldWidth;
    this.worldHeight = worldHeight;
  }

  setTarget(x: number, y: number): void {
    this.targetPosition = { x, y };
  }

  setZoom(zoom: number): void {
    this.zoom = Math.max(0.25, Math.min(2, zoom));
  }

  getZoom(): number {
    return this.zoom;
  }

  update(): void {
    const dx = this.targetPosition.x - this.currentPosition.x;
    const dy = this.targetPosition.y - this.currentPosition.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance < 1) {
      this.currentPosition.x = this.targetPosition.x;
      this.currentPosition.y = this.targetPosition.y;
    } else {
      const smoothing = 0.08;
      this.currentPosition.x += dx * smoothing;
      this.currentPosition.y += dy * smoothing;
    }

    let offsetX = this.viewportWidth / 2 - this.currentPosition.x * this.zoom;
    let offsetY = this.viewportHeight / 2 - this.currentPosition.y * this.zoom;

    // Allow the view to drift up to half a screen past the world edges so the
    // camera can pan a little beyond (instead of stopping hard at the edge).
    const marginX = this.viewportWidth / 2;
    const marginY = this.viewportHeight / 2;
    const maxOffsetX = marginX;
    const minOffsetX = this.viewportWidth - this.worldWidth * this.zoom - marginX;
    const maxOffsetY = marginY;
    const minOffsetY = this.viewportHeight - this.worldHeight * this.zoom - marginY;

    if (this.worldWidth * this.zoom > this.viewportWidth) {
      offsetX = Math.max(minOffsetX, Math.min(maxOffsetX, offsetX));
    } else {
      offsetX = (this.viewportWidth - this.worldWidth * this.zoom) / 2;
    }

    if (this.worldHeight * this.zoom > this.viewportHeight) {
      offsetY = Math.max(minOffsetY, Math.min(maxOffsetY, offsetY));
    } else {
      offsetY = (this.viewportHeight - this.worldHeight * this.zoom) / 2;
    }

    this.container.position.set(offsetX, offsetY);
    this.container.scale.set(this.zoom);
  }

  screenToWorld(screenX: number, screenY: number): Vector2 {
    return {
      x: (screenX - this.container.position.x) / this.zoom,
      y: (screenY - this.container.position.y) / this.zoom,
    };
  }

  worldToScreen(worldX: number, worldY: number): Vector2 {
    return {
      x: worldX * this.zoom + this.container.position.x,
      y: worldY * this.zoom + this.container.position.y,
    };
  }

  resize(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;
  }

  pan(dx: number, dy: number): void {
    this.currentPosition.x += dx / this.zoom;
    this.currentPosition.y += dy / this.zoom;
    this.targetPosition.x = this.currentPosition.x;
    this.targetPosition.y = this.currentPosition.y;

    this.currentPosition.x = Math.max(0, Math.min(this.worldWidth, this.currentPosition.x));
    this.currentPosition.y = Math.max(0, Math.min(this.worldHeight, this.currentPosition.y));
    this.targetPosition.x = this.currentPosition.x;
    this.targetPosition.y = this.currentPosition.y;
  }

  shake(intensity: number, duration: number): void {
    const originalX = this.container.position.x;
    const originalY = this.container.position.y;
    const startTime = performance.now();

    const shakeFrame = () => {
      const elapsed = performance.now() - startTime;
      if (elapsed >= duration) {
        this.container.position.x = originalX;
        this.container.position.y = originalY;
        return;
      }

      const progress = elapsed / duration;
      const currentIntensity = intensity * (1 - progress);

      this.container.position.x = originalX + (Math.random() - 0.5) * currentIntensity;
      this.container.position.y = originalY + (Math.random() - 0.5) * currentIntensity;

      requestAnimationFrame(shakeFrame);
    };

    requestAnimationFrame(shakeFrame);
  }
}
